/**
 * Carrusel de promociones del checkout.
 *
 * <p><strong>La regla que manda sobre el resto:</strong> esto es publicidad dentro de una pagina de
 * pago, asi que NUNCA puede estorbar al pago. En concreto: no se pinta si no hay promos, no ocupa el
 * sitio del boton de pagar, no roba el foco, no dispara peticiones y no puede lanzar una excepcion
 * que tumbe el render de la pantalla. Si algo de esto entra en conflicto con que la publicidad se
 * vea, gana el pago.
 *
 * <p><strong>Accesibilidad,</strong> porque un carrusel es el patron mas facil de hacer mal:
 *
 * <ul>
 *   <li>La rotacion automatica se DETIENE con el raton encima y con el foco dentro. Un carrusel que
 *       cambia mientras alguien intenta pulsar un enlace es una trampa.
 *   <li>Con {@code prefers-reduced-motion: reduce} no rota sola en absoluto. No basta con acortar la
 *       transicion en CSS —eso ya lo hace styles.css—: hay que no mover el contenido.
 *   <li>Los puntos son botones de verdad, alcanzables por teclado, y las flechas izquierda/derecha
 *       navegan cuando el foco esta dentro.
 *   <li>No hay region live: anunciar cada lamina de publicidad interrumpiria a quien esta leyendo el
 *       importe a cobrar.
 * </ul>
 *
 * <p>La fuente de las promos entra por props detras de {@code PromoSource} (ver promos/promos.ts):
 * este componente no sabe si vienen del bundle o de una API.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import type { Promo } from '../promos/promos';
import { PromoArt } from './promoArt';

/** Cada cuanto avanza. 6 s: suficiente para leer una lamina sin que parezca un letrero luminoso. */
const ROTATION_MS = 6000;

export interface PromoCarouselProps {
  readonly promos: readonly Promo[];
  /** Etiqueta accesible del bloque entero, ya traducida por quien lo monta. */
  readonly label: string;
  readonly className?: string | undefined;
}

/**
 * Lee la preferencia de movimiento reducido UNA vez y se suscribe a sus cambios.
 *
 * <p>Se consulta con guarda porque `matchMedia` no existe en el entorno de los tests (jsdom lo
 * expone, pero un doble mas pobre no) y un carrusel no es motivo para romper una suite.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return (): void => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export function PromoCarousel({ promos, label, className }: PromoCarouselProps): JSX.Element | null {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const total = promos.length;
  // El indice se guarda tambien en una ref para que el temporizador no tenga que reprogramarse en
  // cada avance: con `index` como dependencia, el intervalo se reiniciaba y la primera lamina duraba
  // el doble que las demas.
  const indexRef = useRef(0);
  indexRef.current = index;

  const goTo = useCallback(
    (next: number): void => {
      if (total > 0) {
        setIndex(((next % total) + total) % total);
      }
    },
    [total],
  );

  useEffect(() => {
    if (total <= 1 || paused || reducedMotion) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, ROTATION_MS);
    return (): void => window.clearInterval(timer);
  }, [total, paused, reducedMotion]);

  // Si la lista se acorta (la fuente devolvio menos promos), el indice puede quedar fuera de rango.
  useEffect(() => {
    if (total > 0 && indexRef.current >= total) {
      setIndex(0);
    }
  }, [total]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
  }

  // Sin promos no hay bloque: ni marco vacio, ni hueco reservado, ni titulo suelto.
  if (total === 0) {
    return null;
  }

  const active = promos[index] ?? promos[0];
  if (active === undefined) {
    return null;
  }

  return (
    <section
      aria-roledescription="carousel"
      aria-label={label}
      className={className ?? ''}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      data-promo-index={index}
    >
      <div
        className={
          'relative overflow-hidden rounded-[var(--apipay-radius,12px)] ' +
          'ring-1 ring-black/5 shadow-sm'
        }
      >
        {/*
          La relacion de aspecto se fija en el contenedor y no en la imagen: asi el hueco mide lo
          mismo antes y despues de pintar, y el boton de pagar no se mueve bajo el dedo de nadie.
        */}
        <div className="aspect-[16/10] w-full">
          <PromoSlide promo={active} />
        </div>
      </div>

      {total > 1 ? (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {promos.map((promo, position) => {
            const current = position === index;
            return (
              <button
                key={promo.id}
                type="button"
                aria-label={promo.alt}
                aria-current={current}
                onClick={() => goTo(position)}
                className={
                  'h-2 rounded-full transition-all focus-visible:outline focus-visible:outline-2 ' +
                  'focus-visible:outline-offset-2 ' +
                  (current
                    ? 'w-5 bg-[var(--apipay-color-primary,#3a4fd9)]'
                    : 'w-2 bg-black/15 hover:bg-black/30')
                }
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Una lamina. Con `href` es un enlace; sin el, contenido sin mas.
 *
 * <p>El enlace sale SIEMPRE a una pestana nueva con `rel="noopener noreferrer"`: una promocion no
 * puede navegar la pagina donde se esta pagando —seria perder el pago a medias— ni recibir una
 * referencia a esta ventana.
 */
function PromoSlide({ promo }: { readonly promo: Promo }): JSX.Element {
  const art = <PromoArt art={promo.art} />;

  if (promo.href === undefined || promo.href === '') {
    return (
      <div className="h-full w-full" role="img" aria-label={promo.alt}>
        {art}
      </div>
    );
  }

  return (
    <a
      href={promo.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={promo.alt}
      className="block h-full w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {art}
    </a>
  );
}
